import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'

type ImageTranslate = { x: number; y: number }
type ImageTransformValues = { scale: number; rotate: number; translate: ImageTranslate }

export type UseImageTransformOptions = {
  minScale?: number
  maxScale?: number
  step?: number
}

/**
 * 图片预览变换能力（缩放 / 旋转 / 拖拽平移 / 滚轮缩放）。
 * 使用 requestAnimationFrame + 缓动插值将变换应用到 DOM，避免高频 setState 导致卡顿。
 */
export function useImageTransform(options: UseImageTransformOptions = {}) {
  const { minScale = 0.2, maxScale = 5, step = 0.15 } = options

  const [scale, setScale] = useState(1)
  const [rotate, setRotate] = useState(0)
  const [translate, setTranslate] = useState<ImageTranslate>({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [isWheelZooming, setIsWheelZooming] = useState(false)

  const clampScale = useCallback(
    (value: number) => Math.min(maxScale, Math.max(minScale, value)),
    [maxScale, minScale]
  )

  const imageElementRef = useRef<HTMLImageElement | null>(null)

  const isPanningRef = useRef(false)
  const ignoreClickUntilRef = useRef(0)
  const wheelEndTimerRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)
  const animateFrameRef = useRef<(() => void) | null>(null)
  const lastNotifyAtRef = useRef(0)

  const currentRef = useRef<ImageTransformValues>({
    scale: 1,
    rotate: 0,
    translate: { x: 0, y: 0 },
  })
  const targetRef = useRef<ImageTransformValues>({
    scale: 1,
    rotate: 0,
    translate: { x: 0, y: 0 },
  })

  const nowMs = useCallback(() => {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now()
    }
    return Date.now()
  }, [])

  const applyTransformToDom = useCallback((values: ImageTransformValues) => {
    const element = imageElementRef.current
    if (!element) return
    element.style.transformOrigin = 'center center'
    element.style.willChange = 'transform'
    element.style.transform = `translate3d(${values.translate.x}px, ${values.translate.y}px, 0) scale(${values.scale}) rotate(${values.rotate}deg)`
  }, [])

  const notifyReactState = useCallback(
    (values: ImageTransformValues, force?: boolean) => {
      const now = nowMs()
      if (!force && now - lastNotifyAtRef.current < 60) return
      lastNotifyAtRef.current = now
      setScale(values.scale)
      setRotate(values.rotate)
      setTranslate(values.translate)
    },
    [nowMs]
  )

  const stopAnimation = useCallback(() => {
    if (typeof window === 'undefined') return
    if (rafRef.current === null) return
    if (typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(rafRef.current)
    }
    rafRef.current = null
  }, [])

  const animateFrame = useCallback(() => {
    rafRef.current = null

    const current = currentRef.current
    const target = targetRef.current

    const lerp = (from: number, to: number, t: number) => from + (to - from) * t
    const near = (a: number, b: number, epsilon: number) => Math.abs(a - b) <= epsilon

    const scaleT = 0.18
    const rotateT = 0.22
    const translateT = isPanningRef.current ? 1 : 0.22

    const nextScale = clampScale(lerp(current.scale, target.scale, scaleT))
    const nextRotate = lerp(current.rotate, target.rotate, rotateT)
    const nextTranslateX = lerp(current.translate.x, target.translate.x, translateT)
    const nextTranslateY = lerp(current.translate.y, target.translate.y, translateT)

    current.scale = nextScale
    current.rotate = nextRotate
    current.translate = { x: nextTranslateX, y: nextTranslateY }

    applyTransformToDom(current)
    notifyReactState(current)

    const done =
      !isPanningRef.current &&
      near(current.scale, clampScale(target.scale), 0.0008) &&
      near(current.rotate, target.rotate, 0.08) &&
      near(current.translate.x, target.translate.x, 0.2) &&
      near(current.translate.y, target.translate.y, 0.2)

    if (done) {
      current.scale = clampScale(target.scale)
      current.rotate = target.rotate
      current.translate = target.translate
      applyTransformToDom(current)
      notifyReactState(current, true)
      return
    }

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      rafRef.current = window.requestAnimationFrame(() => animateFrameRef.current?.())
    }
  }, [applyTransformToDom, clampScale, notifyReactState])

  useEffect(() => {
    animateFrameRef.current = animateFrame
  }, [animateFrame])

  const startAnimation = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      const current = currentRef.current
      const target = targetRef.current
      current.scale = clampScale(target.scale)
      current.rotate = target.rotate
      current.translate = target.translate
      applyTransformToDom(current)
      notifyReactState(current, true)
      return
    }
    if (rafRef.current !== null) return
    rafRef.current = window.requestAnimationFrame(() => animateFrameRef.current?.())
  }, [applyTransformToDom, clampScale, notifyReactState])

  const panRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    startTranslate: ImageTranslate
    moved: boolean
  } | null>(null)

  const zoomTo = useCallback(
    (value: number) => {
      targetRef.current.scale = clampScale(value)
      startAnimation()
    },
    [clampScale, startAnimation]
  )

  const zoomIn = useCallback(() => {
    targetRef.current.scale = clampScale(targetRef.current.scale + step)
    startAnimation()
  }, [clampScale, startAnimation, step])

  const zoomOut = useCallback(() => {
    targetRef.current.scale = clampScale(targetRef.current.scale - step)
    startAnimation()
  }, [clampScale, startAnimation, step])

  const rotateLeft = useCallback(() => {
    targetRef.current.rotate = targetRef.current.rotate - 90
    startAnimation()
  }, [startAnimation])

  const rotateRight = useCallback(() => {
    targetRef.current.rotate = targetRef.current.rotate + 90
    startAnimation()
  }, [startAnimation])

  const reset = useCallback(() => {
    stopAnimation()
    if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
      if (wheelEndTimerRef.current !== null) window.clearTimeout(wheelEndTimerRef.current)
    }
    wheelEndTimerRef.current = null

    const next: ImageTransformValues = { scale: 1, rotate: 0, translate: { x: 0, y: 0 } }
    currentRef.current = next
    targetRef.current = next
    applyTransformToDom(next)
    notifyReactState(next, true)
    panRef.current = null
    setIsPanning(false)
    setIsWheelZooming(false)
    isPanningRef.current = false
  }, [applyTransformToDom, notifyReactState, stopAnimation])

  const onWheel = useCallback(
    (event: ReactWheelEvent) => {
      event.preventDefault()
      setIsWheelZooming(true)
      if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        if (wheelEndTimerRef.current !== null) window.clearTimeout(wheelEndTimerRef.current)
        wheelEndTimerRef.current = window.setTimeout(() => {
          setIsWheelZooming(false)
        }, 140)
      }
      const k = step * 0.008
      const factor = Math.exp(-event.deltaY * k)
      targetRef.current.scale = clampScale(targetRef.current.scale * factor)
      startAnimation()
    },
    [clampScale, startAnimation, step]
  )

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (event.button !== 0) return
      if (currentRef.current.scale <= 1) return

      const target = event.currentTarget as HTMLElement
      target.setPointerCapture(event.pointerId)

      panRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTranslate: targetRef.current.translate,
        moved: false,
      }
      setIsPanning(true)
      isPanningRef.current = true
    },
    []
  )

  const onPointerMove = useCallback((event: ReactPointerEvent) => {
    const pan = panRef.current
    if (!pan || pan.pointerId !== event.pointerId) return

    const dx = event.clientX - pan.startX
    const dy = event.clientY - pan.startY
    if (!pan.moved && Math.hypot(dx, dy) >= 4) pan.moved = true

    targetRef.current.translate = {
      x: pan.startTranslate.x + dx,
      y: pan.startTranslate.y + dy,
    }
    startAnimation()
  }, [startAnimation])

  const onPointerUp = useCallback((event: ReactPointerEvent) => {
    const pan = panRef.current
    if (!pan || pan.pointerId !== event.pointerId) return
    if (pan.moved) {
      ignoreClickUntilRef.current = nowMs() + 260
    }
    panRef.current = null
    setIsPanning(false)
    isPanningRef.current = false
  }, [nowMs])

  const shouldIgnoreClick = useCallback(() => {
    if (isPanningRef.current) return true
    if (isWheelZooming) return true
    return nowMs() < ignoreClickUntilRef.current
  }, [isWheelZooming, nowMs])

  const setImageElement = useCallback(
    (element: HTMLImageElement | null) => {
      imageElementRef.current = element
      if (!element) return
      applyTransformToDom(currentRef.current)
    },
    [applyTransformToDom]
  )

  const info = useMemo(() => {
    return {
      scale,
      rotate,
      translate,
      isPanning,
      isWheelZooming,
    }
  }, [scale, rotate, translate, isPanning, isWheelZooming])

  useEffect(() => {
    return () => {
      if (typeof window === 'undefined') return
      stopAnimation()
      if (wheelEndTimerRef.current !== null && typeof window.clearTimeout === 'function') {
        window.clearTimeout(wheelEndTimerRef.current)
      }
    }
  }, [stopAnimation])

  return {
    ...info,
    zoomIn,
    zoomOut,
    zoomTo,
    rotateLeft,
    rotateRight,
    reset,
    onWheel,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    setImageElement,
    shouldIgnoreClick,
  }
}
